import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TableCell,
    TableRow,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Typography,
    Chip,
    LinearProgress,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Refresh as RefreshIcon,
    Sync as SyncIcon,
} from '@mui/icons-material';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { AppButton } from '../../../shared/components/AppButton';
import { Loader } from '../../../shared/components/Loader';
import { getOrders, syncOrdersToGoFrugal, getOrdersGoFrugalSyncStatus, GoFrugalSyncProgress } from '../../../shared/services/orderService';
import { userService } from '../../../shared/services/userService';
import { Order as OrderType, GoFrugalSyncStatus } from '../../../shared/types/order';
import { User as UserType } from '../../../shared/types/user.types';
import moment from 'moment';

type SyncTab = 0 | 1; // 0 = Orders, 1 = Users

export const GoFrugalSync: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<SyncTab>(0);

    const [orders, setOrders] = useState<OrderType[]>([]);
    const [totalOrders, setTotalOrders] = useState<number>(0);

    const [users, setUsers] = useState<UserType[]>([]);
    const [totalUsers, setTotalUsers] = useState<number>(0);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [goFrugalSyncStatus, setGoFrugalSyncStatus] = useState<GoFrugalSyncStatus | ''>('');

    const [syncing, setSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState<GoFrugalSyncProgress | null>(null);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const loadOrders = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const params: any = {
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput,
            };
            if (goFrugalSyncStatus) params.goFrugalSyncStatus = goFrugalSyncStatus;

            const response = await getOrders(params);

            if (response.success && response.data) {
                setOrders(response.data.items || []);
                setTotalOrders(response.data.pagination?.totalItems || (response.data.items || []).length);
            } else {
                setOrders([]);
                setTotalOrders(0);
            }
        } catch (err: any) {
            console.error('Error loading GoFrugal sync data:', err);
            setError(err.response?.data?.message || 'Failed to load GoFrugal sync data');
            setOrders([]);
            setTotalOrders(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, goFrugalSyncStatus, searchInput]);

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const params: any = {
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput,
            };
            if (goFrugalSyncStatus) params.goFrugalSyncStatus = goFrugalSyncStatus;

            const response = await userService.list(params);

            if (response.success && response.data) {
                setUsers(response.data.items || []);
                setTotalUsers(response.data.pagination?.totalItems || (response.data.items || []).length);
            } else {
                setUsers([]);
                setTotalUsers(0);
            }
        } catch (err: any) {
            console.error('Error loading GoFrugal sync data:', err);
            setError(err.response?.data?.message || 'Failed to load GoFrugal sync data');
            setUsers([]);
            setTotalUsers(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, goFrugalSyncStatus, searchInput]);

    const loadActiveTabData = useCallback(() => {
        if (activeTab === 0) {
            loadOrders();
        } else {
            loadUsers();
        }
    }, [activeTab, loadOrders, loadUsers]);

    useEffect(() => {
        loadActiveTabData();
    }, [loadActiveTabData]);

    // Stop any in-flight polling on unmount
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, []);

    const getGoFrugalSyncColor = (status?: GoFrugalSyncStatus) => {
        switch (status) {
            case 'SYNCED': return 'success';
            case 'PENDING': return 'warning';
            case 'FAILED': return 'error';
            default: return 'default';
        }
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: SyncTab) => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        setSyncing(false);
        setSyncProgress(null);
        setActiveTab(newValue);
        setSearchInput('');
        setGoFrugalSyncStatus('');
        setCurrentPage(0);
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const handleGoFrugalSyncStatusChange = (event: SelectChangeEvent) => {
        setGoFrugalSyncStatus(event.target.value as GoFrugalSyncStatus | '');
        setCurrentPage(0);
    };

    const resetFilters = () => {
        setSearchInput('');
        setGoFrugalSyncStatus('');
        setCurrentPage(0);
    };

    const handleSync = async () => {
        try {
            setSyncing(true);
            setSyncProgress(null);
            setError('');

            const startSync = activeTab === 0 ? syncOrdersToGoFrugal : userService.syncToGoFrugal;
            const pollStatus = activeTab === 0 ? getOrdersGoFrugalSyncStatus : userService.getGoFrugalSyncStatus;

            await startSync();

            const checkStatus = async () => {
                try {
                    const statusRes = await pollStatus();
                    if (statusRes.success) {
                        setSyncProgress(statusRes.data.progress || null);

                        if (!statusRes.data.isRunning) {
                            if (pollIntervalRef.current) {
                                clearInterval(pollIntervalRef.current);
                                pollIntervalRef.current = null;
                            }
                            setSyncing(false);
                            if (statusRes.data.lastError) {
                                setError(statusRes.data.lastError);
                            }
                            loadActiveTabData();
                        }
                    }
                } catch (err) {
                    console.error('GoFrugal sync polling error:', err);
                    if (pollIntervalRef.current) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                    setSyncing(false);
                }
            };

            await checkStatus();
            pollIntervalRef.current = setInterval(checkStatus, 3000);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to start GoFrugal sync');
            setSyncing(false);
        }
    };

    const isEmpty = activeTab === 0 ? orders.length === 0 : users.length === 0;
    const totalRows = activeTab === 0 ? totalOrders : totalUsers;

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        GoFrugal Sync
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <FormControl variant="outlined" size="small" className="min-w-[150px]">
                        <InputLabel>Sync Status</InputLabel>
                        <Select
                            value={goFrugalSyncStatus}
                            onChange={handleGoFrugalSyncStatusChange}
                            label="Sync Status"
                            disabled={loading}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="PENDING">Pending</MenuItem>
                            <MenuItem value="SYNCED">Synced</MenuItem>
                            <MenuItem value="FAILED">Failed</MenuItem>
                        </Select>
                    </FormControl>

                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder={activeTab === 0 ? 'Search by ID or name' : 'Search by name or email'}
                        className="w-full sm:w-64"
                        disabled={loading}
                    />

                    <Box display="flex" gap={2}>
                        <AppButton
                            variant="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={loadActiveTabData}
                            disabled={loading}
                        >
                            Refresh
                        </AppButton>
                        {(goFrugalSyncStatus || searchInput) && (
                            <AppButton
                                variant="secondary"
                                onClick={resetFilters}
                                disabled={loading}
                            >
                                Clear
                            </AppButton>
                        )}
                        <Tooltip title="Sync GoFrugal is temporarily disabled">
                            <span>
                                <AppButton
                                    variant="primary"
                                    startIcon={<SyncIcon />}
                                    onClick={handleSync}
                                    disabled
                                    loading={syncing}
                                >
                                    {syncing ? 'Syncing...' : 'Sync GoFrugal'}
                                </AppButton>
                            </span>
                        </Tooltip>
                    </Box>
                </div>
            </div>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 10, bgcolor: 'background.paper' }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="gofrugal sync tabs">
                    <Tab label="Orders" />
                    <Tab label="Users" />
                </Tabs>
            </Box>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {syncing && (
                        <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200 flex flex-col gap-3 shadow-sm">
                            <div className="flex items-center gap-3">
                                <CircularProgress size={20} thickness={5} />
                                <span className="text-blue-800 font-semibold italic">
                                    {syncProgress
                                        ? `Syncing... ${syncProgress.processed} / ${syncProgress.total}`
                                        : 'Starting GoFrugal sync... Please wait.'}
                                </span>
                            </div>
                            {syncProgress && (
                                <div className="grid grid-cols-3 gap-4 text-sm bg-white p-3 rounded border border-blue-100">
                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-xs uppercase tracking-wider">Total Pending</span>
                                        <span className="font-bold text-blue-700">{syncProgress.total}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-xs uppercase tracking-wider">Synced</span>
                                        <span className="font-bold text-green-600">{syncProgress.synced}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-xs uppercase tracking-wider">Failed</span>
                                        <span className="font-bold text-red-600">{syncProgress.failed}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {loading && !isEmpty && <LinearProgress className="mb-4" />}

                    {loading && isEmpty ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : isEmpty ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {searchInput.trim() || goFrugalSyncStatus
                                    ? `No ${activeTab === 0 ? 'orders' : 'users'} match your filters.`
                                    : `No ${activeTab === 0 ? 'orders' : 'users'} found.`}
                            </Alert>
                        </Box>
                    ) : activeTab === 0 ? (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Order Information</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Sync Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Reference No</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Synced At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Error</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={orders.length === 0}
                                emptyColSpan={6}
                            >
                                {orders.map((order) => (
                                    <TableRow key={order.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                                {order.orderNumber}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {moment(order.placedAt).format('DD MMM YYYY, hh:mm A')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={order.goFrugalSync?.status || 'PENDING'}
                                                color={getGoFrugalSyncColor(order.goFrugalSync?.status)}
                                                size="small"
                                                className="font-bold"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {order.goFrugalSync?.referenceNo || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {order.goFrugalSync?.syncedAt
                                                    ? moment(order.goFrugalSync.syncedAt).format('DD MMM YYYY, hh:mm A')
                                                    : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {order.goFrugalSync?.error ? (
                                                <Tooltip title={order.goFrugalSync.error}>
                                                    <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 200 }}>
                                                        {order.goFrugalSync.error}
                                                    </Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">-</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View Order">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/orders/view/${order.id}`)}
                                                    sx={{ color: '#0EA5E9' }}
                                                >
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={totalRows}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">User Information</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Sync Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Synced At</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Error</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700" align="right">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={users.length === 0}
                                emptyColSpan={5}
                            >
                                {users.map((user) => {
                                    const rowId = user._id || user.id || user.userId;
                                    return (
                                        <TableRow key={rowId} hover>
                                            <TableCell>
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                                    {user.name || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {user.mobile || user.email || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.goFrugalSync?.status || 'PENDING'}
                                                    color={getGoFrugalSyncColor(user.goFrugalSync?.status)}
                                                    size="small"
                                                    className="font-bold"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {user.goFrugalSync?.syncedAt
                                                        ? moment(user.goFrugalSync.syncedAt).format('DD MMM YYYY, hh:mm A')
                                                        : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {user.goFrugalSync?.error ? (
                                                    <Tooltip title={user.goFrugalSync.error}>
                                                        <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 200 }}>
                                                            {user.goFrugalSync.error}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary">-</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="View User">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/users/view/${rowId}`)}
                                                        sx={{ color: '#0EA5E9' }}
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </AppTable>

                            <AppTablePagination
                                totalRows={totalRows}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setError('')} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </div>
    );
};
